import { CodeableConcept, Coding, Extension } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

/**
 * Builds a FHIR timezone extension from an IANA timezone coding.
 *
 * @param timezone IANA timezone coding.
 * @returns FHIR extension for the timezone value set.
 */
export function buildTimezoneExtension(timezone: Coding): Extension {
  return {
    url: Extensions.Timezone,
    valueCodeableConcept: { coding: [timezone] },
  };
}

/**
 * Returns a resource copy with the timezone extension set or replaced.
 *
 * @param resource FHIR resource that supports extensions.
 * @param timezone IANA timezone coding to persist.
 * @returns Resource with timezone extension applied when coding is present.
 */
export function withTimezoneExtension<T extends { extension?: Extension[] }>(
  resource: T,
  timezone: Coding | undefined,
): T {
  if (!timezone?.code) {
    return resource;
  }

  const extensionsWithoutTimezone = (resource.extension ?? []).filter(
    (extension) => extension.url !== Extensions.Timezone,
  );

  return {
    ...resource,
    extension: [...extensionsWithoutTimezone, buildTimezoneExtension(timezone)],
  };
}

/**
 * Reads the IANA timezone code persisted on a resource by {@link withTimezoneExtension}.
 *
 * @param resource FHIR resource that supports extensions.
 * @returns IANA timezone identifier, or undefined when no timezone extension is present.
 */
export function getTimezoneFromResource(resource: { extension?: Extension[] }): string | undefined {
  const timezoneExtension = resource.extension?.find(
    (extension) => extension.url === Extensions.Timezone,
  );

  return (timezoneExtension?.valueCodeableConcept as CodeableConcept | undefined)?.coding?.[0]
    ?.code;
}
